import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSubscription } from '../context/SubscriptionContext'
import { useState, useEffect, useRef, useMemo } from 'react'
import { subscriptionService, deliveryService, authService } from '../services'
import { PlanDetails, DeliverySchedule, PlanItem } from '../types/api'
import {
  HeroSection,
  FeaturesSection,
  HowItWorksSection,
  OurServicesSection,
  SubscriptionPlansSection,
  CustomerReviewsSection,
  Footer
} from '../components/home'
import ServiceSwitcher from '../components/ServiceSwitcher'
import InstagramEmbed from '../components/common/InstagramEmbed'
import apiClient from '../services/api'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { ChevronRight, Gift } from 'lucide-react'
import OptimizedImageByName from '../components/common/OptimizedImageByName'
import ShutdownNotice from '../components/MaintenanceNotice'

// localStorage key for promotion code
const PROMO_CODE_STORAGE_KEY = 'piika_promo_code';

export default function Fruits() {
  // Use regular t function without namespace specification
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { subscriptions, isLoading: isSubscriptionLoading, error: subscriptionError, renewSubscription, upgradeSubscription } = useSubscription();
  const [planDetails, setPlanDetails] = useState<PlanItem[]>([]);
  const [upcomingDeliveries, setUpcomingDeliveries] = useState<DeliverySchedule[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [promoInfo, setPromoInfo] = useState<string | null>(null);
  const [promoFetching, setPromoFetching] = useState(false);
  const deliveriesFetchedRef = useRef(false);
  const plansLoadedRef = useRef(false);
  const isInitialMount = useRef(true);
  const [reviewFilter, setReviewFilter] = useState<'all' | 'with-photos' | 'five-star' | 'premium' | 'standard'>('all');
  const [expandedPhotoIndex, setExpandedPhotoIndex] = useState<number | null>(null);
  const [reviews, setReviews] = useState<Array<{
    id: string;
    userName: string;
    userInitials: string;
    planType: 'premium' | 'standard';
    date: string;
    rating: number;
    text: string;
    photos: string[];
    helpfulCount: number;
  }>>([]);
  
  // Fetch promotion code details from API
  const fetchPromoDetails = async (code: string) => {
    if (!code || promoFetching) return;
    
    try {
      setPromoFetching(true);
      const response = await fetch(`/api/promotion-codes/${code}`);
      const data = await response.json() as {
        isValid: boolean;
        percentOff?: number;
        amountOff?: number;
        description?: string;
        error?: string;
      };
      
      if (data.isValid) {
        // Set promotion info based on API response
        if (data.percentOff && data.percentOff > 0) {
          setPromoInfo(`${data.percentOff}% OFF`);
        } else if (data.amountOff && data.amountOff > 0) {
          setPromoInfo(`฿${data.amountOff} OFF`);
        } else if (data.description) {
          setPromoInfo(data.description);
        }
      } else {
        // If promo is invalid, clear it from localStorage
        localStorage.removeItem(PROMO_CODE_STORAGE_KEY);
        console.warn('Invalid promotion code:', data.error);
      }
    } catch (err) {
      console.error('Error fetching promotion details:', err);
    } finally {
      setPromoFetching(false);
    }
  };
  
  // Extract promotion code from URL parameters
  useEffect(() => {
    const promoCode = searchParams.get('promo');
    if (promoCode) {
      // Save the promotion code to localStorage
      localStorage.setItem(PROMO_CODE_STORAGE_KEY, promoCode);
      console.log('Promotion code saved to localStorage:', promoCode);
      // Fetch promo details
      fetchPromoDetails(promoCode);
    } else {
      // Check if there's a promo code in localStorage
      const savedPromoCode = localStorage.getItem(PROMO_CODE_STORAGE_KEY);
      if (savedPromoCode) {
        fetchPromoDetails(savedPromoCode);
      }
    }
  }, [searchParams]);

  // Fetch subscription plans and upcoming deliveries
  useEffect(() => {
    console.log('Home useEffect triggered. isSubscriptionLoading:', isSubscriptionLoading, 'subscriptions.length:', subscriptions.length);
    
    const fetchData = async () => {
      // Skip if this is not the initial mount or if already fetched
      if (!isInitialMount.current || (plansLoadedRef.current && deliveriesFetchedRef.current)) {
        console.log('Home: Skipping fetch - not initial mount or already fetched');
        return;
      }
      
      // Ensure we don't fetch again if already loading
      if (isLoading) {
        console.log('Home: Skipping fetch - already loading');
        return;
      }
      
      console.log('Home: Starting fetch. isLoading:', isLoading, 'plansLoaded:', plansLoadedRef.current, 'deliveriesFetched:', deliveriesFetchedRef.current);
      setIsLoading(true);
      setError(null);
      
      try {
        // Always fetch plans - even for non-authenticated users
        if (!plansLoadedRef.current) {
          console.log('Home: Fetching subscription plans...');
          const plans = await subscriptionService.getPlans();
          console.log('Home: Got plans from API');
          setPlanDetails(plans);
          plansLoadedRef.current = true;
        }
        
        // Only fetch deliveries for authenticated users with subscriptions
        if (authService.isAuthenticated() && subscriptions.length > 0 && !deliveriesFetchedRef.current) {
          console.log('Home: Fetching upcoming deliveries...');
          // Pass existing subscriptions to avoid duplicate API call
          const deliveries = await deliveryService.getUpcomingDeliveries(subscriptions);
          console.log('Home: Got deliveries from API');
          setUpcomingDeliveries(deliveries);
          deliveriesFetchedRef.current = true;
        } else if (!authService.isAuthenticated()) {
          // Mark deliveries as fetched for non-authenticated users
          deliveriesFetchedRef.current = true;
          console.log('Home: User not authenticated, skipping deliveries fetch');
        }
        
        // Fetch reviews from the API - anyone can see reviews, logged in or not
        try {
          // Create a separate non-authenticated API instance for public data
          const publicEndpoint = axios.create({
            baseURL: apiClient.defaults.baseURL,
            headers: {
              'Content-Type': 'application/json',
            }
          });
          
          const response = await publicEndpoint.get('/reviews');
          if (response.data && response.data.data) {
            setReviews(response.data.data);
          }
        } catch (reviewError) {
          console.error('Error fetching reviews:', reviewError);
          // Don't set an error state for reviews as they're not critical
        }
      } catch (err) {
        console.error('Error fetching plans:', err);
        setError(t('errors.loadSubscriptionData'));
      } finally {
        setIsLoading(false);
        isInitialMount.current = false;
      }
    };
    
    // Only fetch data if subscriptions finished loading, to avoid race conditions
    if (!isSubscriptionLoading) {
      fetchData();
    }
    
  }, [isSubscriptionLoading, t]);

  // Handle selecting a subscription plan
  const handlePlanSelect = (plan: 'standard' | 'premium') => {
    // Get the saved promotion code from localStorage
    const savedPromoCode = localStorage.getItem(PROMO_CODE_STORAGE_KEY);
    
    // proceed with subscription flow
    navigate('/subscription', { 
      state: { 
        selectedPlan: plan,
        fromPage: 'fruits',
        serviceType: 'fruit',  // Set service type to fruit
        giftType: null,  // Explicitly not a gift
        isGift: false,   // Extra property to ensure it's not treated as a gift
        clearGiftMode: true,  // Explicitly clear any existing gift mode
        // Include the promotion code in the navigation state if available
        promotionCode: savedPromoCode || undefined
      } 
    });
  };

  // Determine the combined loading state
  const isPageLoading = isSubscriptionLoading || isLoading;

  // Add a function to filter reviews
  const filteredReviews = useMemo(() => {
    switch(reviewFilter) {
      case 'five-star':
        return reviews.filter(review => review.rating === 5);
      case 'premium':
        return reviews.filter(review => review.planType === 'premium');
      case 'standard':
        return reviews.filter(review => review.planType === 'standard');
      default:
        return reviews;
    }
  }, [reviews, reviewFilter]);

  // Function to mark review as helpful
  const markReviewAsHelpful = (reviewId: string) => {
    setReviews(reviews.map(review => 
      review.id === reviewId
        ? { ...review, helpfulCount: review.helpfulCount + 1 }
        : review
    ));
  };

  // Function to toggle photo modal
  const togglePhotoModal = (photoIndex: number | null) => {
    setExpandedPhotoIndex(photoIndex);
  };

  // Updated UI component with modern design and better mobile experience
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 ">
      {/* Accessibility skip link */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:p-4 focus:bg-white focus:z-50">
        {t('common.skipToMainContent')}
      </a>

      <ServiceSwitcher 
        isLoading={isPageLoading}
        subscriptions={subscriptions}
      />

      {/* Shutdown Notice */}
      <ShutdownNotice />

      <HeroSection 
        isLoading={isPageLoading}
        subscriptions={subscriptions}
        handlePlanSelect={handlePlanSelect}
        promoInfo={promoInfo}
        serviceType="fruit"
      />

      {/* Choose Your Plan section - moved above Why Choose Piika? */}
      <div className="px-0">
        <SubscriptionPlansSection 
          isLoading={isPageLoading}
          planDetails={planDetails}
          handlePlanSelect={handlePlanSelect}
          promoInfo={promoInfo}
          serviceType="fruit"
        />
      </div>

      {/* Features section with fruit-specific content */}
      <section className="px-6 py-10 bg-white" aria-labelledby="why-choose-piika-fruits">
        <h2 id="why-choose-piika-fruits" className="text-2xl font-bold mb-6 text-center text-gray-900">
          {t('fruits.features.title')}
        </h2>
        
        <div className="space-y-6">
          {[
            {
              icon: <OptimizedImageByName imageName="fruitFarm" alt="Farm Fresh" className="w-20 h-20 object-contain" />,
              title: t('fruits.features.farmFresh.title'),
              description: t('fruits.features.farmFresh.description')
            },
            {
              icon: <OptimizedImageByName imageName="fruitVariety" alt="Fruit Variety" className="w-20 h-20 object-contain" />,
              title: t('fruits.features.weeklyVariety.title'),
              description: t('fruits.features.weeklyVariety.description')
            },
            {
              icon: <OptimizedImageByName imageName="fruitFresh" alt="Always Fresh" className="w-20 h-20 object-contain" />,
              title: t('fruits.features.alwaysFresh.title'),
              description: t('fruits.features.alwaysFresh.description')
            },
            {
              icon: <OptimizedImageByName imageName="fruitGift" alt="Perfect Gift" className="w-20 h-20 object-contain" />,
              title: t('fruits.features.perfectGift.title'),
              description: t('fruits.features.perfectGift.description')
            }
          ].map((feature, index, array) => (
            <div 
              key={index} 
              className={`flex items-center space-x-4 py-4 pl-4 gap-1 ${
                index < array.length - 1 ? 'border-b border-gray-200' : ''
              }`}
              role="listitem"
            >
              <div className="w-20 h-20 rounded-xl flex items-center justify-center flex-shrink-0">
                {feature.icon}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg text-gray-900">{feature.title}</h3>
                <p className="text-gray-600 text-base leading-relaxed">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
      
      {/* Main Content - All sections are now visible */}
      <div id="main-content" className="px-0 bg-gradient-to-b from-white to-gray-100 pt-0">
        {/* Display errors if any */}
        {(error || subscriptionError) && (
          <div className="bg-red-50 border border-red-200 text-gray-800 px-4 py-3 rounded-lg mb-6">
            <p className="font-medium">{t('common.error')}</p>
            <p className="text-sm">{error || subscriptionError}</p>
          </div>
        )}

        {/* Instagram Feed Section */}
        <div className="bg-white py-8">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-800 mb-3">{t('common.instagram.followUs')}</h3>
              <p className="text-gray-600 text-md leading-relaxed">
                {t('common.instagram.gratitudeMessage')}<br/>
                {t('common.instagram.followDescription')}<br/>
                {t('common.instagram.handle')}
              </p>
            </div>
            
            <div className="overflow-x-auto overflow-y-hidden">
              <div className="flex gap-4 px-4 w-max">
                <div className="flex-shrink-0 w-80">
                  <InstagramEmbed
                    postUrl="https://www.instagram.com/p/DJRNzuhNmJq/"
                    captioned={true}
                    maxWidth={320}
                    className="w-full"
                  />
                </div>
                <div className="flex-shrink-0 w-80">
                  <InstagramEmbed
                    postUrl="https://www.instagram.com/p/DI7_MjCvhNh/"
                    captioned={true}
                    maxWidth={320}
                    className="w-full"
                  />
                </div>
                <div className="flex-shrink-0 w-4"></div>
              </div>
            </div>
          </div>
        </div>

        <OurServicesSection serviceType="fruit" />

        <HowItWorksSection />
      </div>

      <Footer />
    </div>
  )
} 